import { useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  EffortMeter,
  METER_ROW_COLUMN,
  useReducedMotion,
} from '../index';
import type { EffortScale } from '@jackedlogs/shared';

import type { ConfirmFn } from '../seams';
import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import { useEffortNudgeEnabled } from './state/effortNudgePreference';
import { useHintsEnabled } from './state/hintsPreference';
import { bandStopFlag } from './state/holdBandStopHint';
import { useOnceFlag } from './state/onceFlag';
import { useEffortNudge } from './useEffortNudge';
import { confirmEffortNudgeOptOut } from './effortNudgeOptOutConfirm';
import { EffortTimerSlot } from './EffortTimerSlot';
import { EffortBandCue, EffortNudgeFrame, EffortNudgeOptOut } from './EffortBandCue';
import {
  EffortDurationAxisLeading,
  EffortDurationAxisTrailing,
} from './EffortAxisDuration';
import type { TimerStartHandlers } from './timerStartHandlers';
import { deriveEffortWording } from './effortWording';
import { makeStyles } from './FocusPanelEffort.styles';

// Effort-meter block of the FocusPanel (AP-0b) — the completion gesture of
// EVERY set kind: one tap on the band = effort + completedAt + PR check +
// rest timer (the composition lives in useEffortCommit; this file is render
// only). Replaces the ✓/✗ commit pair AND the old RIR/RPE text column — one
// write path for effort, no escape hatch.
//
// Two variants of the SAME meter (MX-5 slice 2). A reps set is unchanged: the
// long question between Easy and Hard, Fail in the slot. A duration set (hold
// or cardio) takes the COMPACT variant — the single word "Effort" on the axis,
// the gear where "Hard" was, and the slot handed to the timer (start chip →
// held time). Its band speaks RPE whatever the athlete's scale, because "how
// many reps did you have left" has no answer on a plank (D1); the value saved
// is the same canonical RIR either way, only the printed numbers differ.
//
// LG-19 adds the THIRD variant of the same meter, for the same reason from the
// other side. A set the athlete has marked AMRAP went to technical failure —
// that IS rir 0 — so "how many reps did you have left" is a question whose
// answer is already on the card. On a plank it has no answer; here it has
// exactly one, and offering five stops asks the athlete to re-answer it. So the
// scale collapses to a single wide confirm band (EffortMeter `collapsed`),
// still one tap, still the canonical rir 0, still the only write path. Fail is
// untouched on purpose: "I stopped with nothing left" and "I tried the last one
// and it didn't move" are different facts, and on an AMRAP the second is common
// and true (Antenore 2026-07-23). The variant keys on the OUTCOME
// (DraftSet.amrap, the chip) and never on DraftSet.amrapPrescribed — a set the
// program merely ASKED to be taken to failure keeps the full ramp, because the
// athlete has not said yet that they went there.
//
// Slice 3 gave the compact variant a THIRD state: while the timer runs, the
// axis holds its commands and its clock (EffortAxisDuration) and the slot names what the
// band does. The band stays live, and a tap on it stops the count, writes the
// seconds and records the effort in one gesture — which is what finally closed
// AP-0e, the last way to end a set without being asked how it felt.
// This file coordinates; the CONTENT of each row lives beside it
// (EffortAxisDuration for the axis, EffortTimerSlot for the column,
// EffortBandCue for the pulsing instruction layer): what stays here is WHEN
// each appears and WHAT it says.
//
// The athlete-facing prompt — the RIR or RPE question, whichever scale is
// bound; the wording itself lives in effortWording.ts and is NOT quoted here,
// so this comment cannot go stale when the copy changes (CN-I18N-ALIGN-REST)
// — sits BETWEEN Easy and Hard in the meter's axis row (AP-0j),
// passed to EffortMeter as axisCenter. One tap opens the EffortScaleModal (scale
// anchors + explanation + RIR↔RPE switch) — no separate gear. The
// a11y hints flip with the bound set's state: on a pending set a segment tap
// ALSO logs the set (the screen reader lost the explicit ✓, audit AP-0a); on
// a logged set it only edits the recorded effort.
export function FocusPanelEffort({
  set,
  setKey,
  effortScale,
  value,
  failed,
  onSelect,
  onFail,
  flashRir,
  flashKey,
  onOpenEffortScale,
  isDuration,
  isCardio,
  timer,
  confirm,
  theme,
}: {
  /** The bound set — the meter only renders while one is bound. */
  set: DraftSet;
  /** Identity of the bound set (exercise + set index) — resets the
   *  reminder cadence when the binding moves. */
  setKey: string;
  effortScale: EffortScale;
  /** Canonical rir shown as the selected segment (null = none yet). */
  value: number | null;
  failed: boolean;
  onSelect: (rir: number) => void;
  onFail: () => void;
  /** PR gold pulse passthrough (useEffortCommit → EffortMeter). */
  flashRir: number | null;
  flashKey: number;
  onOpenEffortScale: () => void;
  /** The entry logs a DURATION (timed hold or cardio) ⇒ compact variant. */
  isDuration: boolean;
  /** Within a duration entry: cardio (bout) rather than an isometric hold. */
  isCardio: boolean;
  /** Start-timer affordance for a duration entry — lives in the meter's slot
   *  now, not on a row of its own. Undefined when there's no timer to offer. */
  timer?: TimerStartHandlers;
  /** RW-25 — the host's confirm seam for the opt-out below. Explicit prop, not
   *  LoggerContext: this component is unit-mounted with no Provider around it
   *  (focusPanelEffortNudgeOptOut.test.tsx), so reading a context here would
   *  throw in the very test that guards the control. */
  confirm: ConfirmFn;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const pending = set.completedAt === null;

  // The athlete-facing prompt, the AMRAP wording, and the two collapses that
  // hang off them. Derived in effortWording.ts — pure, and the rationale for
  // each collapse moved there with the code it explains.
  const {
    shownScale,
    questionText,
    effortWord,
    amrapWord,
    doneWord,
    isAmrap,
    shortPrompt,
  } = deriveEffortWording({ isDuration, effortScale, amrap: set.amrap, t });
  const heldSec = parseInt(set.duration, 10) || 0;
  // A timer counting on THIS set: the axis rows swap, and the invitations that
  // belong to a resting meter step aside for the one the band now carries.
  const timerRunning = timer?.running != null;
  // TM-10 (Antenore 2026-07-22): teach the stop-and-log gesture until it has
  // been done once, then get out of the way for good. Re-read whenever a timer
  // starts or stops, which is exactly when the answer could have changed.
  const bandStopDone = useOnceFlag(bandStopFlag, timerRunning);
  const showRunningHint = timerRunning && !bandStopDone;
  // Both captions sit on the BAND, so they have to stop where the band stops:
  // the fixed column is not theirs to cover. Without this the pill centres on
  // the whole row and, on a narrow screen where the column is a bigger share of
  // it, prints over the start chip (seen at 280dp, 2026-07-22). While a timer
  // runs there is no column, so the band is the whole row.
  const captionInset = timerRunning ? 0 : METER_ROW_COLUMN + theme.spacing.s3;

  // Fail press-tip — the auto-dismissing bubble the old ✗ button surfaced
  // (moved here with the commit gesture, AP-0c): explains what "failed"
  // means the moment it's used, gated on the global Hints toggle. Shown
  // only when the result IS failed (no tip when restoring to done).
  const { enabled: hintsEnabled } = useHintsEnabled();
  const [failTipVisible, setFailTipVisible] = useState(false);
  const failTipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (failTipTimer.current) clearTimeout(failTipTimer.current);
    },
    [],
  );
  const handleFail = () => {
    const willFail = pending || !set.fail;
    onFail();
    if (!willFail || !hintsEnabled) return;
    if (failTipTimer.current) clearTimeout(failTipTimer.current);
    setFailTipVisible(true);
    failTipTimer.current = setTimeout(() => setFailTipVisible(false), 4500);
  };

  // Reminder pulse (AP-0d, backed off AP-0q): rare brand frame around the
  // band while the pending set waits for its tap. Off switch in Settings —
  // AND, inline (AP-0r), right here beside the cue it explains, same
  // preference, no second one.
  const { enabled: nudgeEnabled, setEnabled: setNudgeEnabled } =
    useEffortNudgeEnabled();
  const reducedMotion = useReducedMotion();
  const nudgeOpacity = useEffortNudge({
    active: pending,
    setKey,
    enabled: nudgeEnabled,
    reducedMotion,
  });
  // The stop-and-log caption pulses too, on the SAME engine — Antenore's
  // original design, which TM-10 got wrong by making it static: «non solo il
  // bordo del meter lampeggia, ma anche una scritta, che appare e scompare,
  // l'utente legge cosa deve fare, la scritta sparisce, poi ricompare».
  // Measured on the device, the static version was the whole problem: the pill
  // sat permanently over the middle of the band and took stops 7-8-9 down to
  // 1.17:1, so three of five stops were told apart by colour alone in the exact
  // state where you have to pick one. Pulsing costs nothing and fixes it at the
  // root — between cues the band is whole, and the caption is still read.
  //
  // Two differences from the reminder above, both deliberate:
  // - `enabled: true` — the Settings switch governs the decorative REMINDER on
  //   a set you already know how to log. This caption teaches a gesture that
  //   has never been done and retires itself for good the first time it is
  //   (bandStopFlag), so turning the reminder off must not leave a running
  //   timer with no visible way out but the ✕, which discards the seconds.
  // - reduced motion also covers `!nudgeEnabled`: the hook's static branch
  //   shows the caption for 2.5s and then clears it, which is exactly the
  //   "appear, be read, get out of the way" behaviour without the fade.
  const stopHintOpacity = useEffortNudge({
    active: showRunningHint,
    setKey,
    enabled: true,
    reducedMotion: reducedMotion || !nudgeEnabled,
  });

  // The effort prompt — one tap opens the scale explanation + RIR/RPE switch,
  // no separate gear (Antenore: "quando ci tappi apre il tip e anche il
  // settings, non serve il gear"). Same control in both variants; only its
  // WORDING and its place on the axis change (centered question between Easy
  // and Hard on a reps set, the single word at the left on a duration set).
  const questionNode = (
    <Pressable
      // On an AMRAP set the axis is inert LABEL, not a control: the scale is
      // collapsed to one stop, so opening the RIR/RPE switch it no longer
      // offers is a dead end (Antenore smoke 2026-07-23). No button role, no
      // press, no "opens the scale" hint — it just names the test.
      accessibilityRole={isAmrap ? 'text' : 'button'}
      // A duration set still SPEAKS the full question (the RIR wording is
      // wrong there but the control's purpose reads); an AMRAP set must not,
      // because the contradiction is the whole reason this variant exists —
      // a screen reader would hear "reps in reserve?" on a set declared to
      // have none. It hears the word, and (when tappable) the hint says why.
      accessibilityLabel={isAmrap ? amrapWord : questionText}
      accessibilityHint={
        isAmrap
          ? undefined
          : t('mobile.logger.effortQuestionHint', {
              defaultValue:
                'Opens the effort scale — how it works and how to switch it',
            })
      }
      disabled={isAmrap}
      onPress={isAmrap ? undefined : onOpenEffortScale}
      // Horizontal-only slop: minHeight 44 already clears the target
      // vertically, so no vertical slop bleeds into the band below.
      hitSlop={{ left: 8, right: 8 }}
      style={({ pressed }) => [
        styles.questionWrap,
        isDuration && styles.wordWrap,
        pressed && styles.questionPressed,
      ]}
    >
      {/* Only the DURATION variant moves to the axis' left slot, so only it
          takes the left-aligned style; the AMRAP one stays centered where the
          question was. */}
      <Text style={[styles.question, isDuration && styles.word]}>
        {isAmrap ? amrapWord : shortPrompt ? effortWord : questionText}
      </Text>
    </Pressable>
  );

  return (
    <View style={styles.block}>
      {failTipVisible ? (
        <View style={styles.failBubble} pointerEvents="none">
          <Text style={styles.failBubbleText}>
            {t('mobile.timeline.failTip', {
              defaultValue:
                'Fail logs the set as attempted but not completed. It still counts as done (the rest timer runs), shows ✕ in the timeline, and is left out of your e1RM.',
            })}
          </Text>
        </View>
      ) : null}
      <View>
        {/* Reminder frame (AP-0l, Antenore: "rimetti il box che lampeggiava
            attorno alla scala") — the look lives in EffortBandCue; WHEN it
            pulses is this coordinator's call. NOT on an AMRAP set: its band
            reads "Done" statically, so there is nothing to invite and the
            pulse would be the "flashy" Antenore asked to drop (smoke 2026-07-23). */}
        {isAmrap ? null : (
          <EffortNudgeFrame opacity={nudgeOpacity} theme={theme} />
        )}
        <EffortMeter
          theme={theme}
          scale={shownScale}
          value={value}
          failed={failed}
          onSelect={onSelect}
          onFail={handleFail}
          flashRir={flashRir}
          flashKey={flashKey}
          {...(isDuration
            ? {
                // Compact: the word takes the axis LEFT (where "Easy" was), the
                // gear takes the right (where "Hard" was), and the slot goes to
                // the timer. Both end-labels give way — direction still reads
                // from the numbers climbing 6− → 10 (D10/Q2). While a timer
                // runs, both slots hand over to it.
                axisLeading: (
                  <EffortDurationAxisLeading
                    timer={timer}
                    word={questionNode}
                    theme={theme}
                  />
                ),
                axisTrailing: (
                  <EffortDurationAxisTrailing
                    timer={timer}
                    onOpenEffortScale={onOpenEffortScale}
                    theme={theme}
                  />
                ),
                // While the timer counts the column goes away entirely and the
                // band takes the whole row (DS-27): it is the only control that
                // matters then, and on a small screen those points are what
                // keep its segments usable.
                trailingHidden: timerRunning,
                trailing: (
                  <EffortTimerSlot
                    timer={timer}
                    logged={!pending}
                    failed={failed}
                    heldSec={heldSec}
                    isCardio={isCardio}
                    theme={theme}
                  />
                ),
              }
            : { axisCenter: questionNode })}
          // The five stops collapse to one wide confirm band on a declared
          // AMRAP. Deliberately NOT paired with `trailing`/`trailingHidden`
          // (the copy-paste the duration variant invites): either of those
          // deletes the Fail button, and Fail is the fact the collapse does not
          // cover.
          collapsed={isAmrap}
          collapsedLabel={doneWord}
          segmentHint={
            timerRunning
              ? t('mobile.logger.effortMeterHintRunning', {
                  defaultValue: 'Stops the timer and logs the set',
                })
              : !pending
                ? t('mobile.logger.effortMeterHintEdit', {
                    defaultValue: 'Updates the effort of this logged set',
                  })
                : isAmrap
                  ? t('mobile.logger.effortMeterAmrapHint', {
                      defaultValue: 'Logs the set at failure — no reps left',
                    })
                  : t('mobile.logger.effortMeterHintLog', {
                      defaultValue: 'Also logs the set as completed',
                    })
          }
          failHint={
            pending
              ? t('mobile.logger.effortMeterFailHintLog', {
                  defaultValue: 'Logs the set as failed',
                })
              : t('mobile.logger.effortMeterFailHintEdit', {
                  defaultValue: 'Switches this set between done and failed',
                })
          }
        />
        {/* "Tap to log" CTA (AP-0i, inbox ab4502d8) — the mockup's .meter-cta
            pill, restored: a brand-tinted overlay centered on the band, pulsed
            by useEffortNudge (opacity 0 at rest → first blink ~1s after the set
            binds, then rare). pointerEvents none so taps fall through to the
            segments; a11y-hidden because the band's own segment hints already
            say what a tap does. Only while the set is still pending — and NOT
            while a timer runs: there the slot's "Effort" caption already names
            what the band does, and two invitations pulsing at once on one row
            is the noise Q4 warned about. NOT on an AMRAP set: its band prints
            "Done" statically and needs no pulsed invitation (smoke 2026-07-23,
            Antenore: "togli il confirm flashy"). */}
        {pending && !timerRunning && !isAmrap ? (
          <EffortBandCue
            opacity={nudgeOpacity}
            inset={captionInset}
            label={t('mobile.logger.effortMeterTapCta', {
              defaultValue: 'Tap to log the set',
            })}
            theme={theme}
          />
        ) : null}
        {/* The stop-and-log caption (TM-10, repulsed 2026-07-22). Same pill as
            the CTA above and the same slow cue: it appears, is read, gets out
            of the way, comes back. Two things stay different — it says what the
            tap does to the TIMER, and it retires for good the first time the
            athlete does it. Q4's "two invitations at once" never applies here:
            the CTA above is hidden while a timer runs, so only one pill can be
            on the row. Without the caption the gesture is announced to screen
            readers only, and the sighted way out of a running timer reads as
            the ✕ — which discards the seconds. */}
        {showRunningHint ? (
          <EffortBandCue
            opacity={stopHintOpacity}
            inset={captionInset}
            label={t('mobile.logger.effortMeterStopCta', {
              defaultValue: 'Tap to stop and log',
            })}
            // One line: while a timer runs this pill shares the row with the
            // commands, so it must not grow the overlay.
            singleLine
            theme={theme}
          />
        ) : null}
      </View>
      {/* AP-0r: the inline off-switch, same eligibility as the CTA above it
          (pending, no timer running, not the AMRAP collapse — nothing there
          to opt out of) PLUS the preference must still be ON, unlike the
          decorative CTA which stays mounted-but-invisible either way: this
          is a real control, so it has no reason to be reachable once the
          reminder is already off. Normal flow, below the band — never on the
          absolute layer the CTA and frame share with the segments. */}
      {pending && !timerRunning && !isAmrap && nudgeEnabled ? (
        <EffortNudgeOptOut
          label={t('mobile.logger.effortNudgeOptOut', {
            defaultValue: 'Turn off reminder',
          })}
          hint={t('mobile.logger.effortNudgeOptOutHint', {
            defaultValue:
              'Asks you to confirm first. You can turn it back on in Settings.',
          })}
          onPress={() => confirmEffortNudgeOptOut(t, setNudgeEnabled, confirm)}
          theme={theme}
        />
      ) : null}
    </View>
  );
}
